import * as React from "react";
import TeamsBaseComponent, {ITeamsBaseComponentState} from "msteams-react-base-component";
import {
    Checkbox,
    ChoiceGroup,
    ConstrainMode,
    DefaultButton,
    IChoiceGroupOption,
    Icon,
    IDetailsRowProps,
    IIconProps,
    Label,
    MessageBar,
    MessageBarType,
    Panel,
    PanelType,
    PrimaryButton,
    SelectionMode,
    Spinner,
    SpinnerSize,
    TextField,
} from "office-ui-fabric-react";
import {IColumn, ShimmeredDetailsList} from "office-fabric-custom-detail-list"; //this is a custom package made to fix an error with resizing column widths otherwise it is identical to office-ui-fabric-react
import * as microsoftTeams from "@microsoft/teams-js";
import {
    CheckoutFolderInfo,
    DmsCaseInfo,
    FilingLocation,
    IDetailListColumnProps,
    SharedFile,
    StopSharingOptions,
} from "../models";
import GetIcons from "./GetIcons";
import {post, deleteRequest} from "../api";
import {TeamsTab} from "./TeamsTab";
import {NewFileNameTextField} from "./NewFileNameTextField";
import {FileMetadata} from "./FileMetadata";
import RestoreFilePanel from "./RestoreFilePanel";
import LocationItemsListTabView from "./LocationItemsListTabView";
import {NetworkState} from "./ApiCallState";
import {SharedDocumentsContext} from "./components/SharedDocuments";
import { TeamsDocumentStatusTabBanner } from "./statustab/banners/TeamsDocumentStatusTabBanner";

export interface ISharedFilesState extends ITeamsBaseComponentState {
    showStopSharingPanel: boolean;
    shareEndedSuccessfully: boolean;
    checkinComment: string;
    stoppingSharing: boolean;
    stopSharingFile: SharedFile;
    replaceVersion: boolean;
    tenantId: string;
    teamsId: string;
    channelId: string;
    userId: string;
    stopSharingOptions: StopSharingOptions;
    shareEndedFailed: boolean;
    shareFailedMessage: {
        message: string;
        error:
            | "FileInUseException"
            | "DocumentMissingCheckoutException"
            | "AlreadyUploadedException"
            | "InvalidPermissions"
            | "UploadInProgressException";
    };
    showPostShareMessage: boolean;
    showRestorePanel: boolean;
    isNewFileNameError: boolean;
    newFileName: string;
    stopSharingToTeams: boolean;
    teamsLocationSelected: boolean;
    teamNotified: boolean;
    notifyTeamCheckbox: boolean;
    optionalNotifyTeamText: string;
    selectedFolder: FilingLocation;
    notifyTeamError: boolean;
    teamsUrl: string;
}

export interface ISharedFilesProps {
    TeamsId: string;
    StorageType: string;
    DarkMode: boolean;
    ExternalTeamName: string;
    CaseInfo: DmsCaseInfo;
    TeamName: string;
    isLitigationHoldEnabled: boolean;
}

const stopSharingOptions: IChoiceGroupOption[] = [
    {key: "1", text: "Save as new version"},
    {key: "2", text: "Create new document"},
    {key: "3", text: "Discard client changes"},
];

const stopSharingImDestination: IChoiceGroupOption[] = [
    {key: "1", text: "Teams"},
    {key: "2", text: "iManage™"},
];

const stopSharingNdDestination: IChoiceGroupOption[] = [
    {key: "1", text: "Teams"},
    {key: "2", text: "NetDocuments"},
];

const stopSharingSpDestination: IChoiceGroupOption[] = [
    {key: "1", text: "Teams"},
    {key: "2", text: "SharePoint"},
];

const defaultColumnWidths: IDetailListColumnProps[] = [
    {Key: "fileTypeColumn", MinWidth: 20},
    {Key: "fileTitleColumn", MinWidth: 150},
    {Key: "modifiedColumn", MinWidth: 80},
    {Key: "modifiedByColumn", MinWidth: 120},
    {Key: "sharedColumn", MinWidth: 80},
    {Key: "sharedByColumn", MinWidth: 120},
    {Key: "locationColumn", MinWidth: 150},
    {Key: "buttonsColumn", MinWidth: 100},
];

const GridColumnsWidthCache = "SharedListColumns";

export class SharedFiles extends TeamsBaseComponent<ISharedFilesProps, ISharedFilesState> {
    context!: React.ContextType<typeof SharedDocumentsContext>;

    constructor(props: ISharedFilesProps, context: React.ContextType<typeof SharedDocumentsContext>) {
        super(props, context);

        this.state = {
            theme: null,
            showStopSharingPanel: false,
            shareEndedSuccessfully: false,
            checkinComment: "",
            stoppingSharing: false,
            stopSharingFile: null,
            replaceVersion: false,
            tenantId: this.getQueryVariable("tenantId"),
            teamsId: this.getQueryVariable("teamsId"),
            channelId: this.getQueryVariable("channelId"),
            userId: this.getQueryVariable("userId"),
            stopSharingOptions: StopSharingOptions.NewVersion,
            shareEndedFailed: false,
            shareFailedMessage: {message: "", error: null},
            showPostShareMessage: false,
            showRestorePanel: false,
            isNewFileNameError: false,
            newFileName: "",
            stopSharingToTeams: true,
            teamsLocationSelected: false,
            teamNotified: false,
            notifyTeamCheckbox: false,
            optionalNotifyTeamText: "",
            selectedFolder: null,
            notifyTeamError: false,
            teamsUrl: "",
        };
    }

    public async componentWillMount(): Promise<void> {
        microsoftTeams.initialize();
        this.context.getSharedFiles();
        this.context.getTeamsChannels();
    }

    componentDidMount(): void {
        window.localStorage.setItem("ContentConnectorViewKey", "sharedfilestab");
    }

    setStopSharingOption = (val: IChoiceGroupOption) => {
        if (val.key == "1") {
            // For sharepoint, reuse the replace version bool. If it's true, upload as a minor version if it's false upload as a major version
            this.setState({stopSharingOptions: StopSharingOptions.NewVersion});
        } else if (val.key == "2") {
            this.setState({stopSharingOptions: StopSharingOptions.NewDocument});
        } else {
            this.setState({
                stopSharingOptions: StopSharingOptions.DiscardChanges,
                newFileName: this.state.stopSharingFile.FileName,
            });
        }
    };

    setStopSharingDestinationOption = (val: IChoiceGroupOption) => {
        if (val.key == "1") {
            // For sharepoint, reuse the replace version bool. If it's true, upload as a minor version if it's false upload as a major version
            this.setState({stopSharingToTeams: true});
        } else {
            this.setState({stopSharingToTeams: false});
        }
    };

    showStopSharingPanel(item: SharedFile) {
        this.setState({
            showStopSharingPanel: true,
            stopSharingFile: item,
            newFileName: item.ExternalTeamFileName,
            checkinComment: "",
            shareFailedMessage: {message: "", error: null},
        });
    }

    showRestorePanel(item: SharedFile) {
        this.setState({showRestorePanel: true, stopSharingFile: item});
    }

    async removeFileFromList(item: SharedFile) {
        await deleteRequest(
            "/api/teamsdmsintegration/DeleteSharedFileFromSPList?tenantId=" +
                this.state.tenantId +
                "&teamsId=" +
                this.props.TeamsId +
                "&dmsFileId=" +
                item.DmsFileId
        ).then((r) => {
            if (r.status.toString().startsWith("2")) {
                const itemPos = this.context.sharedFiles.indexOf(item);

                const updatedSharedFilesList = this.context.sharedFiles;

                updatedSharedFilesList.splice(itemPos, 1);

                this.context.setParentState({sharedFiles: updatedSharedFilesList});
            }
        });
    }

    async stopSharing() {
        if (this.isFileNameRequired() && this.state.isNewFileNameError) {
            return;
        }

        let checkoutFolderInfo: CheckoutFolderInfo;
        let stopSharingToTeams = false;
        let driveId;

        this.setState({stoppingSharing: true});

        if (this.state.stopSharingToTeams) {
            checkoutFolderInfo = {
                FolderId: this.state.selectedFolder.Metadata["CheckoutFolderId"],
                FolderUrl: this.state.selectedFolder.Url,
                ChannelId: this.state.selectedFolder.Metadata["ChannelId"],
            };
            stopSharingToTeams = true;
            driveId = this.state.selectedFolder.DriveId;
        }

        let fileRenamed = false;

        if (this.state.stopSharingFile.FileName != this.state.newFileName) {
            fileRenamed = true;
        }

        await post("/api/teamsdmsintegration/StopSharingAndRefreshList", {
            TeamsId: this.props.TeamsId,
            TenantId: this.state.tenantId,
            ExternalTeamId: this.state.stopSharingFile.ExternalTeamId,
            DmsFileId: this.state.stopSharingFile.DmsFileId,
            StopSharingOptions: this.state.stopSharingOptions,
            ExternalTeamFileId: this.state.stopSharingFile.ExternalTeamFileId,
            ExternalFileParentFolderId: this.state.stopSharingFile.ExternalFileParentFolderId,
            Comment: this.state.checkinComment,
            FileNameOverride: this.isFileNameRequired() ? this.state.newFileName : null,
            CheckoutFolderInfo: checkoutFolderInfo,
            DriveId: driveId,
            StopSharingToTeams: stopSharingToTeams,
            FileRenamed: fileRenamed,
            ExternalFileEtagAfterUpload: this.state.stopSharingFile.Metadata["ExternalFileEtagAfterUpload"],
            DocumentNumber: this.state.stopSharingFile.Metadata["DocumentNumber"],
        })
            .then((r) => {
                if (r.status.toString().startsWith("2")) {
                    const newSharedFiles = this.context.sharedFiles.filter(
                        (item) => item.DmsFileId !== this.state.stopSharingFile.DmsFileId
                    );
                    this.setState({shareEndedSuccessfully: true, stoppingSharing: false, showPostShareMessage: true});
                    this.context.setParentState({sharedFiles: newSharedFiles});
                } else if (r.status.toString().startsWith("409")) {
                    return r.json();
                } else {
                    this.setState({
                        stoppingSharing: false,
                        shareEndedFailed: true,
                        shareFailedMessage: {message: "Stop sharing failed.", error: null},
                        showPostShareMessage: true,
                    });
                }
                return r.json();
            })
            .then((k) => {
                if (!Object.prototype.hasOwnProperty.call(k, "Error")) {
                    // Response will either be a json string containing error info, or a Teams url string
                    this.setState({teamsUrl: k});
                } else {
                    return k;
                }
            })
            .then((j) => {
                if (j == undefined) {
                    return;
                }
                if (
                    j.Error === "FileInUseException" ||
                    j.Error === "DocumentMissingCheckoutException" ||
                    j.Error === "AlreadyUploadedException" ||
                    j.Error === "InvalidPermissions" ||
                    j.Error === "UploadInProgressException"
                ) {
                    const msg = this.getStopSharingErrorText(j);
                    this.setState({
                        shareEndedFailed: true,
                        shareFailedMessage: {message: msg, error: j.Error},
                        stoppingSharing: false,
                        showPostShareMessage: true,
                    });

                    if (j.Error === "UploadInProgressException") {
                        const sharedFiles = this.context.sharedFiles;
                        const file = sharedFiles.find(
                            (file) => file.Metadata.SPFileId == this.state.stopSharingFile.Metadata.SPFileId
                        );
                        file.IsUploading = true;

                        const stopSharingFile = this.state.stopSharingFile;
                        stopSharingFile.IsUploading = true;
                        this.setState({stopSharingFile});
                    }
                    throw new Error(msg);
                } else {
                    this.setState({
                        shareEndedFailed: true,
                        shareFailedMessage: {message: "Stop sharing failed.", error: j.Error},
                        stoppingSharing: false,
                        showPostShareMessage: true,
                    });
                }
            });

        if (this.state.notifyTeamCheckbox && this.state.stopSharingToTeams) {
            await this.notifyOfCheckout();
        }
    }

    async notifyOfCheckout() {
        await post("/api/teamsdmsintegration/NotifyTeamOfCheckout", {
            TeamId: this.state.teamsId,
            ChannelId: this.state.selectedFolder.Metadata["ChannelId"],
            TenantId: this.state.tenantId,
            OptionalMessage: this.state.optionalNotifyTeamText,
            DocumentName: this.state.newFileName,
            DocUrl: this.state.teamsUrl,
        }).then((r) => {
            if (!r.status.toString().startsWith("2")) {
                this.setState({notifyTeamError: true, teamNotified: true, notifyTeamCheckbox: false});
                throw new Error("Failed to notify team");
            }
        });
        this.setState({teamNotified: true, notifyTeamCheckbox: false, notifyTeamError: false});
    }

    private isFileNameRequired(): boolean {
        return (
            this.state.stopSharingOptions == StopSharingOptions.NewDocument ||
            this.state.stopSharingOptions == StopSharingOptions.NewVersion
        );
    }

    getStopSharingErrorText = (j: {Error: string; InUseUsers: string}) => {
        if (j.Error === "FileInUseException") {
            return "Unable to stop sharing. Document in use by: " + j.InUseUsers;
        } else if (j.Error === "DocumentMissingCheckoutException") {
            return (
                "Unable to stop sharing as this document is no longer checked out in " +
                TeamsTab.getStorageTypeLabel(this.props.StorageType) +
                " to Microsoft Teams."
            );
        } else if (j.Error === "InvalidPermissions") {
            return "Upload failed. Invalid permissions or the destination folder was not found.";
        } else if (j.Error === "UploadInProgressException") {
            return "An upload is already in progress for this file. Please try reloading Content Connector shortly.";
        } else {
            return "Document changed since last check out.";
        }
    };

    openTeamsDocument(file: SharedFile) {
        microsoftTeams.app.openLink(file.UploadedFileUrl);
    }

    getUserColumnMinWidth(key: string) {
        let columnWidths = JSON.parse(window.localStorage.getItem(GridColumnsWidthCache));
        if (!columnWidths) columnWidths = defaultColumnWidths;
        const column = columnWidths.filter((q: IDetailListColumnProps) => q.Key == key)[0];
        const columnSize = column.MinWidth;
        return columnSize;
    }

    public render() {
        let iconDetails: string[] = [];

        if (this.state.showStopSharingPanel) {
            iconDetails = GetIcons.getIconName(this.state.stopSharingFile.FileName, this.props.DarkMode, this.state.stopSharingFile.Metadata, true);
        }

        if (this.context.loadingSharedFiles || this.props.ExternalTeamName == null) {
            return (
                <div>
                    <Spinner label="Loading shared files..." />
                </div>
            );
        } else if (this.context.sharedFiles.length == 0) {
            return (
                <div>
                    {this.props.isLitigationHoldEnabled && (
                        <TeamsDocumentStatusTabBanner
                            isSuccess={false}
                            isError={false}
                            bannerMessage={
                                "Litigation hold enabled for this matter. Sharing features won't work as expected."
                            }
                            onDismiss={null}
                        />
                    )}

                    {this.props.isLitigationHoldEnabled && (
                        <hr style={{height: "10px", visibility: "hidden"}} />
                    )}
                    <p>{this.props.ExternalTeamName}</p>
                    <br></br>
                    <Label style={{fontSize: "16px"}}>No shared files</Label>

                    {this.state.showStopSharingPanel && this.stopSharingPanel(iconDetails)}
                </div>
            );
        } else {
            return (
                <div>
                    <div id="sharedFilesDetailsList" className="suggestion-itemcontainer">
                        {this.props.isLitigationHoldEnabled && (
                            <TeamsDocumentStatusTabBanner
                                isSuccess={false}
                                isError={false}
                                bannerMessage={
                                    "Litigation hold enabled for this matter. Sharing features won't work as expected."
                                }
                                onDismiss={null}
                            />
                        )}

                        {this.props.isLitigationHoldEnabled && (
                            <hr style={{height: "10px", visibility: "hidden"}} />
                        )}
                        <div className="row">
                            <span style={{float: "left"}}>{this.props.ExternalTeamName}</span>
                        </div>
                        <hr style={{height: "10px", visibility: "hidden"}} />
                        <div className="row">
                            <ShimmeredDetailsList
                                setKey="items"
                                className={"dmsItemDetails"}
                                items={this.context.sharedFiles}
                                columns={this.getColumns()}
                                selectionMode={SelectionMode.none}
                                enableShimmer={false}
                                ariaLabelForShimmer="Content is being fetched"
                                ariaLabelForGrid="Item details"
                                onRenderRow={this._renderRow.bind(this)}
                                isHeaderVisible={true}
                                onColumnResize={this.onColumnResize}
                                constrainMode={ConstrainMode.unconstrained}
                            />
                        </div>
                    </div>

                    {this.state.showStopSharingPanel && this.stopSharingPanel(iconDetails)}
                    {this.state.showRestorePanel && (
                        <RestoreFilePanel
                            HideRestorePanel={this.hideRestorePanelAndRefreshList}
                            ShowRestorePanel={this.state.showRestorePanel}
                            DarkMode={this.props.DarkMode}
                            RecycleBinUrl={this.state.stopSharingFile.RecycleBinUrl}
                        />
                    )}
                </div>
            );
        }
    }

    hideRestorePanelAndRefreshList = () => {
        this.setState({showRestorePanel: false});
        this.context.getSharedFiles();
    };

    folderSelected = (value: boolean, location: FilingLocation) => {
        this.setState({teamsLocationSelected: value, selectedFolder: location});
        //this.setState({showShareButton: value, selectedFolder: location, showCheckoutButton: value})
    };

    getStopSharingOptions = () => {
        if (this.props.CaseInfo.StorageType == "imanage") {
            return stopSharingImDestination;
        } else if (this.props.CaseInfo.StorageType == "netdocuments") {
            return stopSharingNdDestination;
        } else {
            return stopSharingSpDestination;
        }
    };

    private stopSharingPanel(iconDetails: string[]) {
        const notifyIcon = {iconName: "Send"};
        const notifyFailureIcon = {iconName: "Error"};

        return (
            <Panel // panel for uploading new documents to the DMS
                isOpen={this.state.showStopSharingPanel}
                className={this.props.DarkMode ? "darkMode" : "lightMode"}
                onDismiss={() =>
                    this.setState({
                        showStopSharingPanel: false,
                        shareEndedSuccessfully: false,
                        shareEndedFailed: false,
                        newFileName: this.state.stopSharingFile.FileName,
                        stopSharingOptions: StopSharingOptions.NewVersion,
                        stopSharingToTeams: true,
                        teamNotified: false,
                    })
                }
                closeButtonAriaLabel="Close"
                customWidth="450px"
                type={PanelType.custom}
                headerText={"Stop sharing"}
            >
                <hr style={{height: "10px", visibility: "hidden"}} />
                <div>
                    {(this.state.shareEndedSuccessfully || this.state.shareEndedFailed) &&
                        this.state.showPostShareMessage && (
                            <MessageBar
                                messageBarType={
                                    this.state.shareEndedSuccessfully ? MessageBarType.success : MessageBarType.error
                                }
                                isMultiline={true}
                                onDismiss={
                                    this.state.shareEndedSuccessfully
                                        ? null
                                        : () => this.setState({showPostShareMessage: false})
                                }
                            >
                                <div style={{fontSize: "14px"}}>
                                    {this.state.shareEndedSuccessfully
                                        ? "Sharing Ended"
                                        : this.state.shareFailedMessage.message}
                                </div>
                            </MessageBar>
                        )}
                    {(this.state.shareEndedSuccessfully || this.state.shareEndedFailed) && (
                        <hr style={{height: "10px", visibility: "hidden"}} />
                    )}

                    {this.state.teamNotified && !this.state.notifyTeamError && (
                        <div>
                            <MessageBar
                                messageBarType={MessageBarType.success}
                                isMultiline={true}
                                messageBarIconProps={notifyIcon}
                            >
                                <div style={{fontSize: "14px"}}>{"Team notified"}</div>
                            </MessageBar>
                            <hr style={{height: "10px", visibility: "hidden"}} />
                        </div>
                    )}

                    {this.state.teamNotified && this.state.notifyTeamError && (
                        <div>
                            <MessageBar
                                messageBarType={MessageBarType.error}
                                isMultiline={true}
                                messageBarIconProps={notifyFailureIcon}
                                onDismiss={() => this.setState({teamNotified: false})}
                            >
                                <div style={{fontSize: "14px"}}>{"Failed to notify Team"}</div>
                            </MessageBar>
                            <hr style={{height: "10px", visibility: "hidden"}} />
                        </div>
                    )}
                    <div className="metadataWrapper">
                        <div className="checkoutIcon">
                            <Icon
                                iconName={iconDetails[0]}
                                style={{fontSize: "24px", marginTop: "6px", color: iconDetails[1]}}
                            />
                        </div>
                        <div className="checkoutFileName preWrap">
                            <p style={{marginBottom: "10px"}}>{this.state.stopSharingFile.FileName}</p>
                        </div>
                    </div>
                    {this.isFileNameRequired() && (
                        <div>
                            <NewFileNameTextField
                                isDisabled={this.state.shareEndedSuccessfully || this.state.stoppingSharing}
                                initialFileName={this.state.newFileName}
                                onErrorCheck={(isError) => this.setState({isNewFileNameError: isError})}
                                onFileNameChange={(fileName) => this.setState({newFileName: fileName})}
                            />
                            <br />
                        </div>
                    )}

                    <ChoiceGroup
                        label="Destination:"
                        className="inlineflex"
                        disabled={this.state.shareEndedSuccessfully || this.state.stoppingSharing}
                        options={this.getStopSharingOptions()}
                        defaultSelectedKey={"1"}
                        onChange={(_, val) => this.setStopSharingDestinationOption(val)}
                    />

                    {this.state.stopSharingToTeams && <br></br>}

                    {!this.state.stopSharingToTeams && (
                        <div>
                            <hr style={{height: "10px", visibility: "hidden"}} />
                            <TextField
                                disabled={this.state.shareEndedSuccessfully || this.state.stoppingSharing}
                                multiline={true}
                                value={this.state.checkinComment}
                                onChange={(_, val) => this.setState({checkinComment: val})}
                                placeholder="Check in comment (optional)"
                            />
                            <hr style={{height: "10px", visibility: "hidden"}} />
                        </div>
                    )}

                    {this.state.stopSharingToTeams && !this.state.shareEndedSuccessfully && (
                        <div>
                            <p style={{fontSize: "16px"}}>
                                {this.props.TeamName} {">"} Channels
                            </p>
                            {this.context.teamsChannelsRetrievalState == NetworkState.Loading && (
                                <Spinner
                                    style={{paddingTop: "15px"}}
                                    size={SpinnerSize.medium}
                                    label="Loading channels..."
                                />
                            )}

                            {this.context.teamsChannelsRetrievalState == NetworkState.Error && (
                                <p style={{color: "red", fontSize: "16px", textAlign: "center"}}>
                                    Teams Channels Retrieval Failed.
                                </p>
                            )}

                            {this.context.teamsChannelsRetrievalState == NetworkState.Success &&
                                !this.state.shareEndedSuccessfully && (
                                    <LocationItemsListTabView
                                        onChooseLocation={null}
                                        chooseThisLocationText={"File Here"}
                                        locations={this.context.teamChannels}
                                        isSearching={false}
                                        onExpand={() => {}}
                                        showResults={true}
                                        searchPhrase={null}
                                        moreResults={false}
                                        folderSearch={false}
                                        localDocs={false}
                                        teamsId={this.state.teamsId}
                                        channelId={this.state.channelId}
                                        storageType={this.props.CaseInfo.StorageType}
                                        userId={this.state.userId}
                                        tenantId={this.state.tenantId}
                                        errorMessage={this.context.errorMessage}
                                        TabView={true}
                                        ShowCheckinPanel={() => {}}
                                        noItemsText="The matter area you are connected to is currently empty"
                                        expandedResults={null}
                                        onBack={null}
                                        darkMode={this.props.DarkMode}
                                        searchFilter={null}
                                        checkoutToChannel={true}
                                        ShowFilingButton={this.folderSelected}
                                        expandedFolderResults={null}
                                        setExpandedResults={() => {}}
                                        shouldIncludeCheckedOutFiles={false}
                                        clientCollabLicensed={true}
                                        searchAllDms={false}
                                        EllipsisOptionOnClick={() => {}}
                                        privateChannel={false}
                                        isLitigationHoldEnabled={false}
                                        isFrictionlessSpExperienceEnabled={false}
                                    />
                                )}
                            <br></br>
                        </div>
                    )}

                    {!this.state.teamNotified &&
                        !this.state.shareEndedSuccessfully &&
                        this.state.stopSharingToTeams && (
                            <div className="notifyTeamLabelFontSize">
                                <Checkbox
                                    checked={this.state.notifyTeamCheckbox}
                                    onChange={(_, val) => this.setState({notifyTeamCheckbox: val})}
                                    label="Post to the channel about this document"
                                />
                                <hr style={{height: "10px", visibility: "hidden"}} />
                            </div>
                        )}

                    {this.state.notifyTeamCheckbox &&
                        this.state.stopSharingToTeams &&
                        !this.state.shareEndedSuccessfully && (
                            <div>
                                <TextField
                                    multiline={true}
                                    onChange={(_, val) => this.setState({optionalNotifyTeamText: val})}
                                    autoComplete="off"
                                    style={{
                                        backgroundColor: this.props.DarkMode ? "black" : "white",
                                        color: this.props.DarkMode ? "white" : "black",
                                    }}
                                    placeholder="Optional comment..."
                                    value={this.state.optionalNotifyTeamText}
                                />

                                <hr style={{height: "10px", visibility: "hidden"}} />
                            </div>
                        )}

                    {!this.state.stopSharingToTeams && (
                        <ChoiceGroup
                            disabled={this.state.shareEndedSuccessfully || this.state.stoppingSharing}
                            defaultSelectedKey={this.getDefaultSelectedKey()}
                            options={stopSharingOptions}
                            onChange={(_, val) => this.setStopSharingOption(val)}
                            required={true}
                        />
                    )}
                    <br></br>

                    {!this.state.shareEndedSuccessfully &&
                        !this.state.stoppingSharing &&
                        !this.state.stopSharingToTeams && (
                            <PrimaryButton
                                disabled={this.state.stopSharingFile.IsUploading}
                                onClick={(_) => this.stopSharing()}
                                style={{textAlign: "center"}}
                                text="Stop sharing"
                            />
                        )}

                    {!this.state.shareEndedSuccessfully &&
                        !this.state.stoppingSharing &&
                        this.state.stopSharingToTeams &&
                        this.state.teamsLocationSelected && (
                            <PrimaryButton
                                disabled={this.state.stopSharingFile.IsUploading}
                                onClick={(_) => this.stopSharing()}
                                style={{textAlign: "center"}}
                                text="Stop sharing"
                            />
                        )}

                    {this.state.shareEndedSuccessfully && (
                        <PrimaryButton
                            onClick={(_) =>
                                this.setState({
                                    showStopSharingPanel: false,
                                    shareEndedSuccessfully: false,
                                    shareEndedFailed: false,
                                    newFileName: this.state.stopSharingFile.FileName,
                                    stopSharingOptions: StopSharingOptions.NewVersion,
                                    stopSharingToTeams: true,
                                    teamNotified: false,
                                })
                            }
                            style={{textAlign: "center"}}
                            text="Ok"
                        />
                    )}

                    {this.state.stoppingSharing && <Spinner />}
                </div>
            </Panel>
        );
    }

    private getDefaultSelectedKey(): string | number {
        return (this.state.stopSharingOptions.valueOf() + 1).toString();
    }

    private _renderRow(
        props: IDetailsRowProps,
        defaultRender: (props?: IDetailsRowProps) => JSX.Element | null
    ): JSX.Element {
        //Standard file rendering
        return <div>{defaultRender({...props})}</div>;
    }

    private onColumnResize(column?: IColumn, newWidth?: number, _columnIndex?: number) {
        let columnWidths = JSON.parse(window.localStorage.getItem(GridColumnsWidthCache));
        if (!columnWidths) columnWidths = defaultColumnWidths;

        const newWidths = columnWidths.map((p: IDetailListColumnProps) =>
            p.Key === column.key ? {...p, MinWidth: newWidth} : p
        );

        const values = JSON.stringify(newWidths);
        window.localStorage.setItem(GridColumnsWidthCache, values);
    }

    private getColumns(): IColumn[] {
        return [
            {
                key: "fileTypeColumn",
                name: "File Type",
                ariaLabel: "File Type",
                isIconOnly: true,
                minWidth: 32,
                maxWidth: 32,
                minResizeWidth: 32,
                onRender: (item) => {
                    const iconDetails = GetIcons.getSharedIcon(item, this.props.DarkMode);
                    return (
                        <Icon
                            iconName={iconDetails[0]}
                            style={{fontSize: "32px", marginTop: "6px", color: iconDetails[1]}}
                        />
                    );
                },
            },
            {
                key: "fileTitleColumn",
                name: "File",
                minWidth: this.getUserColumnMinWidth("fileTitleColumn"), // for the custom package this is actually the current width when loading the component
                minResizeWidth: 150, // the true minimum resize width used
                maxWidth: 300,
                isResizable: true,
                data: "string",
                isPadded: true,
                onRender: (item: SharedFile) => {
                    const folderNameMarginTop = 3;

                    return (
                        <div style={{marginTop: folderNameMarginTop}}>
                            <div
                                title={item.FileName}
                                onClick={() => (!item.IsDeleted ? this.openTeamsDocument(item) : null)}
                                className={item.IsDeleted ? "deletedFile preWrap" : "sharedFileTitle preWrap"}
                            >
                                {item.FileName}
                            </div>

                            <FileMetadata file={item} isDeleted={item.IsDeleted} />
                        </div>
                    );
                },
            },
            {
                key: "modifiedColumn",
                name: "Modified",
                currentWidth: this.getUserColumnMinWidth("modifiedColumn"),
                minWidth: this.getUserColumnMinWidth("modifiedColumn"),
                maxWidth: 200,
                minResizeWidth: 50,
                isResizable: true,
                data: "string",
                isPadded: true,
                onRender: (item) => {
                    if (!item.IsDeleted) {
                        return (
                            <div className="sharedColumnTextCell">
                                <div
                                    className={item.IsDeleted ? "deletedFile" : "textOverflowElipsis"}
                                    title={item.LastModifiedDate}
                                    style={{fontSize: "15px"}}
                                >
                                    {new Date(item.LastModifiedDate).toLocaleDateString()}
                                </div>
                            </div>
                        );
                    }
                },
            },
            {
                key: "modifiedByColumn",
                name: "Modified By",
                currentWidth: this.getUserColumnMinWidth("modifiedByColumn"),
                minWidth: this.getUserColumnMinWidth("modifiedByColumn"),
                maxWidth: 150,
                minResizeWidth: 50,
                isResizable: true,
                data: "string",
                isPadded: true,
                onRender: (item) => {
                    if (!item.IsDeleted) {
                        return (
                            <div className="sharedColumnTextCell">
                                <div
                                    className={item.IsDeleted ? "deletedFile" : "textOverflowElipsis"}
                                    title={item.LastModifiedBy}
                                    style={{fontSize: "15px"}}
                                >
                                    {item.LastModifiedBy}
                                </div>
                            </div>
                        );
                    }
                },
            },
            {
                key: "sharedColumn",
                name: "Shared",
                currentWidth: this.getUserColumnMinWidth("sharedColumn"),
                minWidth: this.getUserColumnMinWidth("sharedColumn"),
                maxWidth: 80,
                minResizeWidth: 50,
                isResizable: true,
                data: "string",
                isPadded: true,
                onRender: (item) => {
                    return (
                        <div className="sharedColumnTextCell">
                            <div
                                className={item.IsDeleted ? "deletedFile" : "textOverflowElipsis"}
                                title={item.SharedDate}
                                style={{fontSize: "15px"}}
                            >
                                {new Date(item.SharedDate).toLocaleDateString()}
                            </div>
                        </div>
                    );
                },
            },
            {
                key: "sharedByColumn",
                name: "Shared By",
                currentWidth: this.getUserColumnMinWidth("sharedByColumn"),
                minWidth: this.getUserColumnMinWidth("sharedByColumn"),
                maxWidth: 150,
                minResizeWidth: 50,
                isResizable: true,
                data: "string",
                isPadded: true,
                onRender: (item) => {
                    return (
                        <div className="sharedColumnTextCell">
                            <div
                                className={item.IsDeleted ? "deletedFile" : "textOverflowElipsis"}
                                title={item.SharedBy}
                                style={{fontSize: "15px"}}
                            >
                                {item.SharedBy}
                            </div>
                        </div>
                    );
                },
            },
            {
                key: "locationColumn",
                name: "Location",
                currentWidth: this.getUserColumnMinWidth("locationColumn"),
                minWidth: this.getUserColumnMinWidth("locationColumn"),
                maxWidth: 350,
                minResizeWidth: 50,
                isResizable: true,
                data: "string",
                isPadded: true,
                onRender: (item) => {
                    return (
                        <div className="sharedColumnTextCell">
                            <div
                                className={item.IsDeleted ? "deletedFile" : "textOverflowElipsis"}
                                title={item.SharedLocation}
                                style={{fontSize: "15px"}}
                            >
                                {item.SharedLocation}
                            </div>
                        </div>
                    );
                },
            },
            {
                key: "buttonsColumn",
                name: "",
                minWidth: 200,
                maxWidth: 200,
                minResizeWidth: 200,
                data: "string",
                isPadded: true,
                onRender: (item) => {
                    if (item.IsDeleted) {
                        const restore: IIconProps = {iconName: "Refresh"};
                        const close: IIconProps = {iconName: "ChromeClose"};
                        return (
                            <div className="sharedColumnStopSharingCell">
                                <DefaultButton
                                    title="Restore file from recycle bin"
                                    iconProps={restore}
                                    onClick={() => this.showRestorePanel(item)}
                                />
                                <DefaultButton
                                    title="Remove file from list"
                                    iconProps={close}
                                    onClick={() => this.removeFileFromList(item)}
                                    style={{marginLeft: "20px"}}
                                />
                            </div>
                        );
                    } else {
                        return (
                            <div className="sharedColumnStopSharingCell">
                                <DefaultButton
                                    disabled={item.IsUploading}
                                    style={{width: "125px"}}
                                    onClick={() => this.showStopSharingPanel(item)}
                                    text={"Stop Sharing"}
                                />
                            </div>
                        );
                    }
                },
            },
        ];
    }
}
SharedFiles.contextType = SharedDocumentsContext;
export default SharedFiles;
